import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import isFunction from 'lodash/isFunction';

import { envConfig } from '../../../config';
import { rootActions } from '../../../store/slices/root.slice';
import { UploadApkFilesProps } from '../../app-store/models/upload-apk-files-props.model';
import { createPathParams } from '../utils/create-path-params/create-path-params.util';
import { Endpoints } from '../utils/endpoints-file.utils';
import { generateId } from '../utils/generate-id/generate-id.util';
import { getChunkUploadStatus } from '../utils/get-chunk-upload-status/get-chunk-upload-status.util';
import {
  mapApkFileMetadataDtoToFileMetadataModel,
  mapFileMetadataDtoToFileMetadataModel,
} from '../utils/mapping/mapping.utils';
import { sendFileChunks } from '../utils/send-file-chunks.util';
import { sendFile } from '../utils/send.file';

export const uploadApkFiles: AsyncThunk<
  void,
  UploadApkFilesProps,
  Record<string, unknown>
> = createAsyncThunk(
  'upload-apk-files',
  async (
    {
      file,
      onFileUploadCallback,
      onFileRequestCallback,
      fileType,
      splitIntoChunks = false,
      onSuccess,
      onError,
      appId,
      apkInfo,
    }: UploadApkFilesProps,
    thunkAPI
  ) => {
    thunkAPI.dispatch(rootActions.toggleApkFileUploadVisibility());
    const shouldSplitIntoChunks =
      splitIntoChunks && file.size >= envConfig.app.chunkSize;
    const correlationId = shouldSplitIntoChunks ? generateId() : null;

    const url = createPathParams<'appId'>(
      shouldSplitIntoChunks && correlationId
        ? Endpoints.BASE_LARGE_APK_FILES_PATH
        : Endpoints.BASE_APK_FILES_PATH,
      {
        appId,
      }
    );

    const resp =
      shouldSplitIntoChunks && correlationId
        ? await sendFileChunks(file, fileType, url, correlationId, apkInfo)
        : await sendFile(file, fileType, url, apkInfo);

    if (isFunction(onFileUploadCallback)) {
      onFileUploadCallback(file);
    }

    if (shouldSplitIntoChunks && resp.data.id) {
      const result = await getChunkUploadStatus(
        resp.data.id,
        onSuccess,
        onError
      );

      thunkAPI.dispatch(
        onFileRequestCallback(
          mapApkFileMetadataDtoToFileMetadataModel(result.data)
        )
      );
      thunkAPI.dispatch(rootActions.toggleApkFileUploadVisibility());
      return;
    }

    thunkAPI.dispatch(
      onFileRequestCallback(mapFileMetadataDtoToFileMetadataModel(resp.data))
    );
    thunkAPI.dispatch(rootActions.toggleApkFileUploadVisibility());
  }
);
